import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../../core/config/environment";
import { db } from "../../../../../core/database/db";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";

@Injectable({ providedIn: "root" })
export class CmLotService {
  private readonly archiveLotUrl = `${environment.backend.apiBaseURL}/api/lot`;

  constructor(
    private http: HttpClient,
    private toastService: UiToastService
  ) { }

  /**
   * Archives a lot: calls the API to mark it archived, then removes all
   * associated data from the local Dexie store.
   */
  async archiveLot(lotId: number): Promise<boolean> {
    if (!!navigator.onLine === false) {
      this.toastService.info("You are currently offline. Please connect to the internet to archive a lot.");
      return false;
    }

    try {
      await firstValueFrom(
        this.http.post(`${this.archiveLotUrl}/${lotId}/archive`, null, { responseType: "text" })
      );
      await this.removeLotDataFromDevice(lotId);
      this.toastService.success("Lot archived and removed from your device.");
      return true;
    } catch {
      this.toastService.error("An error occurred while archiving the lot. Please try again.");
      return false;
    }
  }

  /**
   * Unarchives a lot: calls the API to restore it. The next sync will
   * download the lot data back to the device.
   */
  async unarchiveLot(lotId: number): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`${this.archiveLotUrl}/${lotId}/unarchive`, null, { responseType: "text" })
      );
      this.toastService.success("Lot unarchived. It will appear on your device after the next sync.");
      return true;
    } catch {
      this.toastService.error("An error occurred while unarchiving the lot. Please try again.");
      return false;
    }
  }

  /**
   * Removes all data associated with a lot from the local Dexie store.
   */
  private async removeLotDataFromDevice(lotId: number): Promise<void> {
    const jobs = await db.inspectionJob.where("lotId").equals(lotId)
      .toArray();
    const jobIds = jobs.map(j => j.id);

    if (jobIds.length > 0) {
      const jobIssues = await db.jobIssue.where("jobId").anyOf(jobIds)
        .toArray();
      const issueIdentifiers = jobIssues
        .map(i => i.identifier)
        .filter((id): id is string => !!id);

      if (issueIdentifiers.length > 0) {
        await db.jobIssueVendor.where("issueIdentifier").anyOf(issueIdentifiers)
          .delete();
      }

      await Promise.all([
        db.jobIssue.where("jobId").anyOf(jobIds)
          .delete(),
        db.assertionIssue.where("jobId").anyOf(jobIds)
          .delete(),
        db.assertion.where("lotId").equals(lotId)
          .delete(),
        db.component.where("lotId").equals(lotId)
          .delete(),
        db.jobSignature.where("jobId").anyOf(jobIds)
          .delete(),
      ]);
    }

    const zones = await db.zone.where("lotId").equals(lotId)
      .toArray();
    const zoneIds = zones.map(z => z.id);
    if (zoneIds.length > 0) {
      await db.area.where("zoneId").anyOf(zoneIds)
        .delete();
    }

    await db.zone.where("lotId").equals(lotId)
      .delete();
    await db.inspectionJob.where("lotId").equals(lotId)
      .delete();
    await db.lot.delete(lotId);
  }
}
